import { HttpErrorResponse } from '@angular/common/http';
import { Component, OnDestroy, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { AllLinesCannotBeRouted, MessageError, SomeLinesCannotBeRouted } from '@core/constants/messages';
import { AuthenticationService, LookupService, MemberService, QueueReviewType, QueueService, TenantService } from 'src/app/core';
import { MemberLiasionId, SecondLevelReviewHH } from '@shared/model';
import { ArrayUtilities } from '@shared/utility';
import { FeatureToggleService, ODataService } from '@mynexus/mynexus-ui-lib';
import { mapValues } from 'lodash-es';
import { ConfirmationService, LazyLoadEvent, MessageService, SelectItem } from 'primeng/api';
import { DialogService, DynamicDialogConfig } from 'primeng/dynamicdialog';
import { Table, TableHeaderCheckbox } from 'primeng/table';
import { Observable, Subject, Subscription, catchError, finalize, forkJoin, map, of, switchMap, tap } from 'rxjs';
import { SubSink } from 'subsink';
import { EMPTY_OPTION, ZZ_SKILL_CODE_OPTION, tableConfig } from './constants/table.config';
import { HHServiceLineAuthorizationLevel, HHServiceLineDTO, HHServiceLineResponse } from './models';
import { DoNextQueueDialogComponent } from '@shared/component/do-next-queue-dialog/do-next-queue-dialog.component';

@Component({
    // eslint-disable-next-line @angular-eslint/component-selector
    selector: 'hh-platform-queue',
    templateUrl: './hh-platform-queue.component.html',
    styleUrls: ['./hh-platform-queue.component.scss'],
    standalone: false
})
export class HHPlatformQueueComponent implements OnInit, OnDestroy {
  @ViewChild(Table, { static: true }) table: Table = null;

  @ViewChild('headerCheckBox') private headerCheckBox: TableHeaderCheckbox;

  @ViewChild('input', { static: true, read: TemplateRef }) inputTemplate: TemplateRef<any>;
  @ViewChild('multiselect', { static: true, read: TemplateRef }) multiselectTemplate: TemplateRef<any>;
  @ViewChild('dropdown', { static: true, read: TemplateRef }) dropdownTemplate!: TemplateRef<any>;

  first = 0;
  totalRecords = 0;
  selectedRows: HHServiceLineDTO[] = [];
  tableSettings = {
    stateKey: 'hh-platform-queue',
    rowsPerPage: 25,
    dataKey: 'serviceLineQueueId',
  };

  filterTemplates: Record<string, TemplateRef<any>>;
  filtersData: Record<any, SelectItem[]> = {
    ['user/userId']: null,
    requestType: null,
    serviceLineStatus: null,
    serviceLineStatusReason: null,
    tenantName: null,
    hcpcCode: null,
    orderSubtype: null,
    memberState: null,
    isQualityCheckComplete: ArrayUtilities.arrayToSelectItemArrayByItem(['false', 'true']),
  };

  usersListForRouting: SelectItem[];
  currentUser: SelectItem[];
  currentUserId: string;
  hhRelatedGroup = 'HHOperations';

  isLoading = false;
  isRoutingDialogVisible = false;
  isLoadingManualRoute = false;
  memberIdAndMemberLiasonId: MemberLiasionId[] = null;
  queueData$: Observable<HHServiceLineDTO[]> = null;
  memberIdAndMemberLiasonId$: Subscription;
  dialogIsOpen = false;
  selectedReview: SecondLevelReviewHH;
  get routingDisabled(): boolean {
    return this.selectedRows?.length === 0 || this.isLoading;
  }

  readonly columnConfig = tableConfig;
  readonly millisecondsInDay = 86400000; // (1000 * 3600 * 24)
  readonly notAvailableStatusForRemove = ['Pending', 'Incomplete'];
  readonly ADJUDICATION_STATUSES = ['Approved', 'Cancelled', 'Dismissed', 'Non-Admit', 'Denied', 'Partially Favorable'];

  private loadQueueData$: Subject<LazyLoadEvent> = new Subject();

  private readonly subs = new SubSink();
  private isMemberLiasionAvailable: boolean = false;

  get memberLiasionAvailable(): boolean{
    return this.isMemberLiasionAvailable;
  }

  set memberLiasionAvailable(value: boolean){
    this.isMemberLiasionAvailable = value;
    if (this.isMemberLiasionAvailable) {

       this.sendToWCCAssignedLiason();
       this.isMemberLiasionAvailable = false;

    }
  }

  constructor(
    private queueService: QueueService,
    private oDataService: ODataService,
    private router: Router,
    private tenantService: TenantService,
    private lookupService: LookupService,
    private authenticationService: AuthenticationService,
    private confirmationService: ConfirmationService,
    private messageService: MessageService,
    private dialogService: DialogService,
    private memberService: MemberService,
    private readonly featureService: FeatureToggleService
  ) {}

  ngOnInit(): void {

    this.currentUserId = this.authenticationService.oid;

    this.getTenants();
    this.getFilters();

    this.filterTemplates = {
      input: this.inputTemplate,
      multiselect: this.multiselectTemplate,
      dropdown: this.dropdownTemplate,
    };

    this.removeSessionForCalculatingElapseTime();

    this.queueData$ = this.loadQueueData$.pipe(
      tap(() => (this.isLoading = true)),
      map(query => this.optimizeQuery(query)),
      map(query => this.oDataService.buildQuery(query)),
      switchMap(query => this.queueService.getHHQueueData(query)),
      tap(data => {
        this.setSessionForCalculatingElapseTime();
        this.totalRecords = data.count;
        this.isLoading = false;
      }),
      map(this.mapTableResponse.bind(this)),
    );
  }

  fetchTableData(event: LazyLoadEvent): void {
    if (event.sortField) {
      event.multiSortMeta = event.sortField === 'memberName'
        ? [
            { field: 'urgencySort', order: 1 },
            { field: 'memberName', order: event.sortOrder },
            { field: 'requestReceivedDate', order: event.sortOrder },
          ]
        : event.sortField === 'urgencySort'
        ? [
            { field: 'urgencySort', order: event.sortOrder },
          ]
        : [
            { field: 'urgencySort', order: 1 },
            { field: event.sortField, order: event.sortOrder },
          ];

      event.sortField = null;
      event.sortOrder = null;
    }

    this.loadQueueData$.next(event);
  }

  headerCheckboxToggle(event) {
    this.headerCheckBox.checked = event.checked;
  }

  reloadQueue(): void {
    this.selectedRows = [];
    const event = this.table.createLazyLoadMetadata();
    event.sortField = null;
    event.sortOrder = null;
    event.multiSortMeta = [
      { field: 'urgencySort', order: 1 },
      { field: 'requestReceivedDate', order: 1 },
    ];
    this.loadQueueData$.next(event);
  }

  setDefaultTableState(): void {
    this.setTableState(this.getDefaultFiltersState());
  }

  filterTable(value: string, field: string, matchMode: string): void {
    this.table.filter(value, field, matchMode);

    this.selectedRows = [];
  }

  navigateToMemberPage(serviceLine: HHServiceLineDTO): void {
    this.router.navigate([
      this.tenantService.getByName(serviceLine.tenantName).tenantId,
      'member',
      serviceLine.memberSubscriberNumber,
    ]);
  }

  navigateToAuthorization(serviceLine: HHServiceLineDTO): void {
    this.router.navigate([
      '/',
      this.tenantService.getByName(serviceLine.tenantName).tenantId,
      'home-health',
      'auth-dashboard',
      serviceLine.authorizationNumber,
    ]);
  }

  navigateToServiceLine(serviceLine: HHServiceLineDTO): void {
    this.router.navigate([
      '/',
      this.tenantService.getByName(serviceLine.tenantName).tenantId,
      'home-health',
      'auth-dashboard',
      serviceLine.authorizationNumber,
      serviceLine.serviceLineId,
    ]);
  }

  showRouteToQueueDialog(): void {
    this.isRoutingDialogVisible = true;
  }

  closeRouteDialog(): void {
    this.isRoutingDialogVisible = false;
  }

  sendToResourceRouter(isManualRouting: boolean = true): void {
    this.confirmationService.confirm({
      header: 'Confirmation',
      message: 'Selected line(s) will be sent to Resource Router.',
      acceptLabel: 'Submit',
      accept: () => {
        this.isLoading = true;

        forkJoin(
          this.selectedRows.map(row => {
            return this.queueService.routeToResourceRouter(row.serviceLineQueueId, isManualRouting);
          })
        ).pipe(
          finalize(() => this.isLoading = false)
        ).subscribe({
          next: () => {
            this.messageService.add({
              severity: 'success',
              summary: 'Lines Routed',
              detail: 'All lines sent to Holding Queue.',
              life: 10000,
            });

            this.reloadQueue();
          },
          error: (error: HttpErrorResponse) => this.handleError(error),
        });
      },
    });
  }

  removeFromQueue(isManualRouting: boolean = true): void {
    let line = '';

    this.removeDuplicates(this.selectedRows.map(item => item.user.name)).forEach((name: string) => {
      line += `<li>${name}</li>`;
    });

    const messageText = `
      <span class="bold-text">Current Queue:</span>
      <ul class="custom-list">${line}</ul>
    `;

    const availableSl = this.selectedRows.filter(item => !this.notAvailableStatusForRemove.includes(item.serviceLineStatus));

    this.confirmationService.confirm({
      header: 'Remove from Queue',
      message: messageText,
      acceptLabel: 'Remove from Queue',
      accept: () => {
        this.deleteServiceLines(availableSl, isManualRouting);
      },
    });
  }

  private deleteServiceLines(serviceLines: HHServiceLineDTO[], isManualRouting): void {
    if (serviceLines.length > 0) {
      this.isLoading = true;

      forkJoin(
        serviceLines.map(line => this.queueService.removeFromQueue(line.serviceLineQueueId, isManualRouting))
      ).pipe(
        finalize(() => {
          this.isLoading = false;
          this.reloadQueue();
        })
      ).subscribe({
        next: () => {
          if (this.selectedRows.length === serviceLines.length) {
            this.messageService.add({
              severity: 'success',
              summary: 'Removed from Queue',
              detail: 'Data was updated successfully.',
              life: 5000,
            });
          } else {
            this.removeFromQueueErrorMessage();
          }
        },
        error: (error: HttpErrorResponse) => {
          this.handleError(error);
        },
      });
    } else {
      this.removeFromQueueErrorMessage();
    }
  }

  sendToUser(userId: string, serviceLines: HHServiceLineDTO[], secondLevelReview: string, isManualRouting: boolean = true): void {
    this.isLoadingManualRoute = true;

    forkJoin(
      serviceLines.map(line => {
        return this.queueService.sendToUserQueue(
          line.serviceLineQueueId,
          userId,
          secondLevelReview,
          isManualRouting
        ).pipe(catchError(() => of(line.serviceLineQueueId)));
      })
    ).pipe(
      finalize(() => this.isLoadingManualRoute = false)
    ).subscribe({
      next: (data) => {
        const allRouted = data.every(res => res === null);
        const allFailed = !data.includes(null);

        if (allRouted) {
          this.messageService.add({
            severity: 'success',
            summary: 'Lines Routed',
            detail: 'All lines routed to user.',
            life: 5000,
          });

          this.isRoutingDialogVisible = false;
          this.reloadQueue();
        } else {
          const messageDetail = allFailed
            ? AllLinesCannotBeRouted
            : SomeLinesCannotBeRouted;

          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: messageDetail,
            life: 5000,
          });

          this.selectedRows = this.selectedRows.filter(item => data.includes(item.serviceLineQueueId))
        }
      },
    });
  }

  private removeDuplicates(userList: string[]): string[] {
    const typesList = userList.reduce((acc, cur) => {
      if (cur && !acc.includes(cur)) {
        acc.push(cur);
      }

      return acc;
    }, []);

    return typesList;
  }

  private handleError(error?: HttpErrorResponse): void {
    if (error?.status === 409) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Warning',
        detail: MessageError,
        life: 5000,
      });
    } else {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'No service lines were routed',
        life: 5000,
      });
    }
  }

  private removeFromQueueErrorMessage(): void {
    this.messageService.add({
      severity: 'error',
      summary: 'Pending/Incomplete service lines cannot be removed',
      detail: 'Pending/Incomplete service lines must be moved to a specific queue or to the Resource Router.',
      life: 5000,
    });
  }

  private getFilters(): void {
    this.subs.sink = forkJoin([
      this.getDropdownReferences(),
      this.getUsers(),
    ]).subscribe(() => {
      this.tableStateInit();

      const storageState = JSON.parse(this.table.getStorage().getItem(this.tableSettings.stateKey));

      this.setTableState(storageState);
    });
  }

  private getDropdownReferences(): Observable<void> {
  return this.queueService.getHHQueueReferences().pipe(
    map((refs) => mapValues(refs, (ref) => ref.map((value) => ({ value, label: value })))),
    tap((refs) => {
      this.filtersData = {
        ...this.filtersData,
        ...refs,
        memberState: [EMPTY_OPTION, ...refs.memberState],
        requestType: [EMPTY_OPTION, ...refs.requestType],
        serviceLineStatusReason: [EMPTY_OPTION, ...refs.serviceLineStatusReason],
        orderSubtype: [EMPTY_OPTION, ...refs.orderSubtype],
        skillCode: [ZZ_SKILL_CODE_OPTION, ...refs.skillCode].sort((a, b) => a.label.localeCompare(b.label)),
      };
    }),
    catchError(() => {
      this.filtersData = {};

      return of(null);
    }),
    map(() => undefined),
  );
}

  private getUsers(): Observable<void> {
    return this.lookupService.getUsersByGroup(this.hhRelatedGroup).pipe(
      map(users => users
        .map(user => {
          const { objectId, surname, givenName } = user;

          return { value: objectId, label: `${givenName} ${surname || ''}` } as SelectItem;
        })
        .sort((firstValue, secondValue) => firstValue.label.localeCompare(secondValue.label))),
      tap(users => {
        this.usersListForRouting = users;

        this.currentUser = users.filter(
          ({ value }) => value === this.currentUserId
        );

        const usersList = [
          ...this.currentUser,
          ...users.filter(item => item.value !== this.currentUserId),
        ];

        this.filtersData = { ...this.filtersData, ...{ ['user/userId']: usersList } };
      }),
      catchError(() => {
        this.filtersData = {};

        return of(null);
      }),
      map(() => undefined),
    );
  }

  private optimizeQuery(meta: LazyLoadEvent): LazyLoadEvent {
    return {
      ...meta,
      filters: meta?.filters
        ? Object.entries(meta.filters).reduce((acc, [key, value]) => {

          // Remove filter if all items are selected
          if (Array.isArray(value.value)) {
            const itemsInFilter = this.filtersData[key]?.length;
            const itemsInQuery = value.value.length;

            if (!itemsInFilter || itemsInFilter && itemsInFilter !== itemsInQuery) {
              return { ...acc, [key]: value };
            } else {
              return { ...acc };
            }
          } else {
            return { ...acc, [key]: value };
          }
        }, {})
        : {},
        multiSortMeta: meta.sortField ? null : meta.multiSortMeta,
    };
  }

  private mapTableResponse(data: HHServiceLineResponse) {
  const currentTime = new Date().getTime();

  return data.items.map((item) => {
    const isescalted = item.isEscalatedCase;
    const timeDifference =
      currentTime - new Date(item.requestReceivedDate).getTime();

    const isTimeOverdue =
      !isescalted &&
      this.checkRedHighlighting(item.urgency, timeDifference);

    const isAdCompleteOverdue =
      !isescalted &&
      !isTimeOverdue &&
      item.anticipatedManualDecisionDate &&
      (Date.now() > new Date(item.anticipatedManualDecisionDate).getTime()) &&
      (item.urgency === 'Routine' || item.urgency === 'Urgent') && (item.serviceLineStatus==='Pending') &&
      (item.serviceLineStatusReason === '1st Outreach' || item.serviceLineStatusReason === 'Escalated Review Request');

    const isTimeApproaching =
      !isescalted &&
      !isTimeOverdue &&
      !isAdCompleteOverdue &&
      this.checkYellowHighlighting(item.urgency, timeDifference);

    const isBlue =
      !isescalted &&
      !isTimeOverdue &&
      !isAdCompleteOverdue &&
      !isTimeApproaching &&
      this.checkBlueHighlighting(
        item.serviceLineStatus,
        item.serviceLineStatusReason
      );

    return {
      ...item,
      userName: item.user.name,
      userId: item.user.userId,
      highlightedInRed: isTimeOverdue,
      adCompleteOverdue: isAdCompleteOverdue,
      highlightedInYellow: isTimeApproaching,
      highlightedInBlue: isBlue,
      timeRemaining: this.checkTimeRemainingForTimer(item),
      shouldShowWccWarning: item.isWccWarning && item.authorizationLevel === HHServiceLineAuthorizationLevel.Active,
      showTimeOutWarning: this.showTimeOutWarningForTimer(item,currentTime),
      outreachReasonsTooltip: this.generateOutreachReasonsTooltip(item.outreachReasons),
      timeTakenForReviewAdjudication: item.clinicalReviewAdjudicationDate ?
      this.timeTakenForReviewAdjudication(item.clinicalReviewStartDate,item.clinicalReviewAdjudicationDate
          )
        : 0,
    };
  });
}

  private checkTimeRemainingForTimer(item: HHServiceLineDTO): boolean {
    return this.checkTimeRemaining(item) && !this.checkOutreach(item);
  }

  private showTimeOutWarningForTimer(item: HHServiceLineDTO, currentTime: number): boolean {
    return this.checkClinicalReviewStartDate(item);
  }



  private checkOutreach(item: HHServiceLineDTO): boolean {
    return ['Escalated Review Request', '1st Outreach'].some(status => item?.serviceLineStatusReason?.includes(status));
  }
  private checkTimeRemaining(item: HHServiceLineDTO): boolean {
    return item.anticipatedManualDecisionDate
      && item.serviceLineStatus === 'Pending'
      && ['Urgent', 'Routine'].includes(item.urgency) && !item.clinicalReviewStartDate;
  }

  private checkClinicalReviewStartDate(item: HHServiceLineDTO) {
   return item.clinicalReviewStartDate && item.anticipatedManualDecisionDate && ['Urgent', 'Routine'].includes(item.urgency);
  }

  private getTenants(): void {
    this.tenantService.getAll().subscribe();
  }

  private checkRedHighlighting(urgency: string, time: number): boolean {
    const daysSpent = Math.floor(time / this.millisecondsInDay);

    return (urgency === 'Urgent' && daysSpent >= 2) || (urgency !== 'Urgent' && daysSpent >= 10);
  }

  private checkYellowHighlighting(urgency: string, time: number): boolean {
    const daysSpent = Math.floor(time / this.millisecondsInDay);

    return (urgency === 'Urgent' && daysSpent >= 1) || (urgency !== 'Urgent' && daysSpent >= 7);
  }

  private checkBlueHighlighting(status: string, reason: string): boolean {
    return status === 'Pending' && reason === 'Peer To Peer';
  }

  private tableStateInit() {
    const storage = this.table.getStorage();

    try {
      const state = JSON.parse(storage.getItem(this.tableSettings.stateKey));

      if (!state) {
        storage.setItem(
          this.tableSettings.stateKey,
          JSON.stringify(this.getDefaultFiltersState())
        );

        return;
      }
    } catch (error) {
      storage.setItem(
        this.tableSettings.stateKey,
        JSON.stringify(this.getDefaultFiltersState())
      );
    }
  }

  private getDefaultFiltersState() {
    return {
      filters: this.currentUser?.[0]
        ? { ['user/userId']: {
          value: [this.currentUser[0].value],
          matchMode: 'in',
        }}
        : {},
      first: 0,
      globalFilter: null,
      rows: this.tableSettings.rowsPerPage,
      multiSortMeta: [
        { field: 'urgencySort', order: 1 },  // Ascending urgency
        { field: 'requestReceivedDate', order: 1 },    // Ascending requestReceivedDate
    ],
      sortField: null,
      sortOrder: 1,
      selection: [],
    };
  }

  generateOutreachReasonsTooltip(arr: string[]): string {
    if (!arr || arr.length === 0) {
      return '';
    }

    const listItems = arr.map((el: string) => `<li>${el}</li>`).join('');

    return `<div class="tooltip-header">Outreach Reasons:</div><ul>${listItems}</ul>`;
  }

  setTableState(state): void {
    const { sortOrder, sortField,multiSortMeta, filters } = state;

    this.selectedRows = [];
    this.table.lazy = false;
    this.table.clear();
    this.table.filters = filters || {};
    this.table.selection = [];
    this.table.sortOrder = sortOrder;
    this.table.sortField = sortField;
    this.table.multiSortMeta = multiSortMeta;
    this.table.totalRecords = this.totalRecords;
    this.table.saveState();
    this.table.restoringFilter = false;
    this.table.lazy = true;

    if (multiSortMeta) {
      this.table.sortMultiple();
    } else {
      this.loadQueueData$.next(this.table.createLazyLoadMetadata());
    }
  }

    showDoNextDialog(): void {
    this.dialogIsOpen = true;

    const dialogConfig: DynamicDialogConfig = {
      header: 'Do Next',
      style: {
        'min-width': '500px',
        'min-height': '10vh',
        'box-shadow': 'none'
      },
      minY: 80,
      modal: true,
      closable: true,
      maximizable: false,
      draggable: true,
    };

    const dialogRef = this.dialogService.open(DoNextQueueDialogComponent, dialogConfig);

     this.subs.sink = dialogRef.onClose.subscribe((selectedReview: SecondLevelReviewHH) => {
      if (selectedReview) {
        this.routeQueuesForDoNext(selectedReview);
      }
      this.dialogIsOpen = false;
    });
  }

  routeQueuesForDoNext(selectedReview: SecondLevelReviewHH): void {
      this.selectedReview = selectedReview;
      const isManualRouting = false;

      switch (selectedReview.reviewValue) {
        case QueueReviewType.CompleteRemove:
             this.removeFromQueue(isManualRouting);

          break;
        case QueueReviewType.Self:
             this.sendToUser(this.currentUserId,this.selectedRows, selectedReview.reviewValue, isManualRouting);

          break;
        case QueueReviewType.ResourceRouter:
             this.sendToResourceRouter(isManualRouting);

          break;
        case QueueReviewType.WCCAssignedLiason:
             this.getAllMembers();

          break;
        default:
             this.sendToHoldingQueue(selectedReview.reviewValue, isManualRouting);
      }
  }


  sendToHoldingQueue(reviewValue, isManualRouting: boolean = true): void {
    this.confirmationService.confirm({
      header: 'Confirmation',
      message: 'Selected line(s) will be sent to Holding Queue.',
      acceptLabel: 'Submit',
      accept: () => {
        this.isLoading = true;

        forkJoin(
          this.selectedRows.map(row => {
            return this.queueService.routeToHoldingQueue(row.serviceLineQueueId, reviewValue, isManualRouting);
          })
        ).pipe(
          finalize(() => this.isLoading = false)
        ).subscribe({
          next: () => {
            this.messageService.add({
              severity: 'success',
              summary: 'Lines Routed',
              detail: 'All lines sent to Holding Queue.',
              life: 10000,
            });

            this.reloadQueue();
          },
          error: (error: HttpErrorResponse) => this.handleError(error),
        });
      },
    });
  }

  getAllMembers(){
     const memberIds = this.selectedRows.map(row => row.memberId);
     if(this.memberIdAndMemberLiasonId$){
        this.memberIdAndMemberLiasonId$.unsubscribe();
     }
     this.memberIdAndMemberLiasonId$ = this.memberService.getAllMembers(this.tenantService.getByName(this.selectedRows[0].tenantName).tenantId, memberIds).pipe()
     .subscribe(result => {
      this.memberIdAndMemberLiasonId = result;
      this.sendToWCCAssignedLiason();
    });
  }

  getTheAssignedLiasionIdByName(memberId: string): string{
    const member = this.memberIdAndMemberLiasonId.find(x => x.memberId == memberId);
    if(member.assignedLiaisonName){
      const assignedLiaisonName = this.usersListForRouting.find(x => x.label == member.assignedLiaisonName);
      if(assignedLiaisonName)
        return assignedLiaisonName.value;
    }
    return null;
  }

  sendToWCCAssignedLiason() {
    this.isLoadingManualRoute = true;
    let failedServiceLines = 0;
    forkJoin(
      this.selectedRows.map(line => {
         const assignedName = this.getTheAssignedLiasionIdByName(line.memberId);

            if (!assignedName) {
              failedServiceLines++;
              return of(null);
            }
        return this.queueService.sendToUserQueue(
          line.serviceLineQueueId,
          assignedName,
          this.selectedReview.reviewValue,
          false
        ).pipe(catchError(() => of(line.serviceLineQueueId)));
      })
    ).pipe(
      finalize(() => this.isLoadingManualRoute = false)
    ).subscribe({
      next: (data) => {
        if (failedServiceLines == 0) {
          this.messageService.add({
            severity: 'success',
            summary: 'Lines Routed',
            detail: 'All lines routed to user.',
            life: 5000,
          });

          this.isRoutingDialogVisible = false;
          this.reloadQueue();
        } else {
          const messageDetail = failedServiceLines == this.selectedRows.length
            ? AllLinesCannotBeRouted
            : SomeLinesCannotBeRouted;

          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: messageDetail,
            life: 5000,
          });

          this.selectedRows = this.selectedRows.filter(item => data.includes(item.serviceLineQueueId))
        }
      },
    });
  }

  timeTakenForReviewAdjudication(clinicalReviewStartDate, clinicalReviewAdjudicationDate): number{
    return new Date(clinicalReviewAdjudicationDate).getTime() - new Date(clinicalReviewStartDate).getTime();
  }

  setSessionForCalculatingElapseTime(){
    sessionStorage.setItem('elapsedTimeFetchedAt', Date.now().toString());
  }

  removeSessionForCalculatingElapseTime(){
    sessionStorage.removeItem('elapsedTimeFetchedAt');
  }
  ngOnDestroy(): void {
    this.subs.unsubscribe();
    if(this.memberIdAndMemberLiasonId$){
       this.memberIdAndMemberLiasonId$.unsubscribe();
    }
  }
}
